import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Target, RotateCcw, Download, Check } from 'lucide-react';
import { formatNumber, formatMoney } from '../utils';
import { buildDayGroups, computeExecutedRows } from '../utils/planLogic';
import { useLanguage } from '../contexts/LanguageContext';
import { Toast } from './Toast';
import { PlanHeader } from './PlanHeader';
import { ResetConfirmModal, ExtraCapitalModal } from './PlanModals';

export function PlanTable({ 
  bitacoraEntries, 
  estudioEntries = [],
  percentage, 
  initialCapital = 0,
  onInitialCapitalChange,
  confirmedExtractions = {},
  onConfirmExtraction,
  onUndoExtraction,
  onReset,
  onUndo,
  canUndo,
  extraDeposits = [],
  onAddDeposit,
  onDeleteDeposit,
  accountAdjustment = 0,
  onAccountAdjustmentChange
}) {
  const { t, language } = useLanguage();
  const [isDownloading, setIsDownloading] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmCapitalChange, setConfirmCapitalChange] = useState(false);
  const [pendingCapitalValue, setPendingCapitalValue] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [pendingExtraction, setPendingExtraction] = useState(null);
  const [pendingUndoExtraction, setPendingUndoExtraction] = useState(null);
  const [showDepositsModal, setShowDepositsModal] = useState(false);
  const scrollRef = useRef(null);
  const currentRowRef = useRef(null);
  const didAutoScroll = useRef(false);
  
  // Local state for capital input (allows user to type freely)
  const [capitalInputValue, setCapitalInputValue] = useState(initialCapital || 0);
  const [hasUnsavedCapital, setHasUnsavedCapital] = useState(false);
  
  // Sync local state with prop changes
  useEffect(() => {
    setCapitalInputValue(initialCapital || 0);
    setHasUnsavedCapital(false);
  }, [initialCapital]);
  
  // Group ALL entries (bitacora + estudio/negative) by DATE: one plan trade per day
  const dayGroups = useMemo(
    () => buildDayGroups(bitacoraEntries, estudioEntries),
    [bitacoraEntries, estudioEntries]
  );

  // Calculate total account value and available balance
  const accountStats = useMemo(() => {
    // Total Cuenta mirrors the real broker balance: capital + deposits + full real
    // profits (incl. Profit+) - full losses - confirmed extractions (via accountDelta).
    // Extra deposits: money added on a given day, counts from that day forward
    const totalDeposits = extraDeposits.reduce((sum, d) => sum + (d.amount || 0), 0);
    const rows = computeExecutedRows({
      dayGroups, percentage, initialCapital, confirmedExtractions,
      startingBalance: initialCapital + totalDeposits + accountAdjustment
    });
    const totalProfit = rows.reduce((s, r) => s + r.accountDelta, 0);
    
    const totalExtracted = Object.values(confirmedExtractions).reduce(
      (sum, ex) => sum + (ex.amount || 0), 0
    );
    
    // Total Cuenta can never be shown negative (raw value kept for manual edits)
    const rawTotalAccount = initialCapital + totalDeposits + totalProfit + accountAdjustment;
    const totalAccount = Math.max(0, rawTotalAccount);
    
    // Available for investment = 1/3 of total
    const availableBalance = totalAccount / 3;
    
    return {
      totalAccount,
      rawTotalAccount,
      availableBalance,
      totalProfit,
      totalExtracted,
      totalDeposits
    };
  }, [dayGroups, percentage, initialCapital, confirmedExtractions, extraDeposits, accountAdjustment]);

  const planRows = useMemo(() => {
    // Executed rows: streaks, cuts and extraction cancellation (shared logic).
    // currentTotal anchors days without saved inversión to the account's real total.
    const rows = [...computeExecutedRows({
      dayGroups, percentage, initialCapital, confirmedExtractions,
      startingBalance: initialCapital + (accountStats.totalDeposits || 0),
      currentTotal: accountStats.rawTotalAccount
    })];
    // Rolling forecast: 100 rows base, silently extended by 50 for every 50
    // executed trades (50 -> 150, 100 -> 200, ...) to avoid wasting memory
    const rowCount = Math.max(100 + Math.floor(dayGroups.length / 50) * 50, dayGroups.length + 1);

    for (let index = rows.length; index < rowCount; index++) {
      const isCurrentTrade = index === dayGroups.length; // Next trade to execute
      let investment;
      if (isCurrentTrade) {
        // For the NEXT trade (current): use the CURRENT available balance
        investment = accountStats.availableBalance;
      } else {
        // For future trades: compound from previous
        const prevInvestment = rows[index - 1]?.investment || accountStats.availableBalance;
        investment = prevInvestment + prevInvestment * (percentage / 100);
      }
      const expectedProfit = investment * (percentage / 100);

      rows.push({
        index: index + 1,
        investment,
        expectedProfit,
        dayGroup: undefined,
        date: '',
        realProfit: 0,
        investedReal: 0,
        opsCount: 0,
        profitPlus: 0,
        extraction: 0,
        originalExtraction: 0,
        extractionCancelled: false,
        extractionReduced: false,
        cutBalance: null,
        streak: 0,
        streakBroken: false,
        isExecuted: false,
        isCurrentTrade
      });
    }

    // Executed rows already carry their real day total (anchored in planLogic).
    // Future rows chain from the current real total plus expected profits.
    let running = accountStats.rawTotalAccount;
    rows.forEach((r) => {
      if (r.isExecuted) return;
      r.accountTotal = Math.max(0, running);
      running += r.expectedProfit;
    });
    return rows;
  }, [dayGroups, percentage, accountStats.availableBalance, accountStats.totalDeposits, initialCapital, confirmedExtractions, accountAdjustment]);

  // On open, center the current trade in view (only once per mount)
  useEffect(() => {
    if (didAutoScroll.current) return;
    const container = scrollRef.current;
    const row = currentRowRef.current;
    if (!container || !row) return;
    didAutoScroll.current = true;
    container.scrollTop = Math.max(0, row.offsetTop - container.clientHeight / 2 + row.clientHeight / 2);
  }, [planRows]);

  // Pending (unconfirmed) extractions for the reminder banner
  const pendingExtractions = useMemo(() => {
    const pending = planRows.filter(r => r.isExecuted && r.extraction > 0 && !confirmedExtractions[r.index]);
    return {
      count: pending.length,
      total: pending.reduce((sum, r) => sum + r.extraction, 0)
    };
  }, [planRows, confirmedExtractions]);

  // Sorted withdrawal history (memoized, avoids sorting on every render)
  const sortedExtractions = useMemo(
    () => Object.entries(confirmedExtractions).sort((a, b) => new Date(b[1].date) - new Date(a[1].date)),
    [confirmedExtractions]
  );

  const downloadAsCSV = () => {
    console.log('[DEBUG] Plan CSV button clicked - entries:', bitacoraEntries.length);
    
    if (planRows.filter(row => row.isExecuted).length === 0) {
      setToastMessage(language === 'es' ? '⚠️ No hay datos para descargar' : '⚠️ No data to download');
      setToastType('error');
      setShowToast(true);
      return;
    }
    
    setIsDownloading(true);
    
    try {
      const headers = [
        'Trade',
        language === 'es' ? 'Fecha' : 'Date',
        language === 'es' ? 'Total Cuenta' : 'Total Account',
        language === 'es' ? 'Inversión' : 'Investment',
        language === 'es' ? 'Invertido Real' : 'Real Invested',
        '%',
        'Profit',
        language === 'es' ? 'Profit Total' : 'Total Profit',
        'Profit +',
        language === 'es' ? 'Extracción' : 'Extraction'
      ];

      const rows = planRows.filter(row => row.isExecuted).map(row => [
        `Trade ${row.index}`,
        row.date || '',
        (row.accountTotal ?? 0).toFixed(2),
        row.investment.toFixed(2),
        row.investedReal.toFixed(2),
        `${percentage}%`,
        (row.streakBroken ? row.realProfit : row.expectedProfit).toFixed(2),
        row.realProfit.toFixed(2),
        row.streakBroken || row.profitPlus <= 0 ? '' : row.profitPlus.toFixed(2),
        row.extraction !== 0 ? row.extraction.toFixed(2) : ''
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      console.log('[DEBUG] Plan CSV content length:', csvContent.length, 'rows:', rows.length);

      // Add BOM for UTF-8 encoding
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const fileName = `plan_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      
      // Add to DOM for better browser compatibility
      document.body.appendChild(link);
      link.click();
      
      // Show success toast
      setTimeout(() => {
        setToastMessage(language === 'es' 
          ? `✅ Archivo descargado: ${fileName}` 
          : `✅ File downloaded: ${fileName}`);
        setToastType('success');
        setShowToast(true);
      }, 200);
      
      // Cleanup with delay
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        setIsDownloading(false);
        console.log('[SUCCESS] Plan CSV download complete:', fileName);
      }, 500);
    } catch (error) {
      console.error('[ERROR] Plan CSV download failed:', error);
      setIsDownloading(false);
      setToastMessage(language === 'es' 
        ? '❌ Error al descargar archivo' 
        : '❌ Download failed');
      setToastType('error');
      setShowToast(true);
    }
  };

  const downloadHistoryCSV = () => {
    const entries = Object.entries(confirmedExtractions)
      .sort((a, b) => new Date(a[1].date) - new Date(b[1].date));
    
    if (entries.length === 0) return;
    
    try {
      const headers = [
        '#',
        'Trade',
        language === 'es' ? 'Fecha de Retiro' : 'Withdrawal Date',
        language === 'es' ? 'Monto' : 'Amount'
      ];
      
      const rows = entries.map(([tradeIndex, ex], i) => [
        i + 1,
        `Trade ${tradeIndex}`,
        new Date(ex.date).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
        }),
        (ex.amount || 0).toFixed(2)
      ]);
      
      const total = entries.reduce((sum, [, ex]) => sum + (ex.amount || 0), 0);
      rows.push(['', '', language === 'es' ? 'TOTAL' : 'TOTAL', total.toFixed(2)]);
      
      const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const fileName = `retiros_${new Date().toISOString().split('T')[0]}.csv`;
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      
      setToastMessage(language === 'es' 
        ? `✅ Historial descargado: ${fileName}` 
        : `✅ History downloaded: ${fileName}`);
      setToastType('success');
      setShowToast(true);
      
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      }, 500);
    } catch (error) {
      setToastMessage(language === 'es' ? '❌ Error al descargar' : '❌ Download failed');
      setToastType('error');
      setShowToast(true);
    }
  };

  const handleReset = () => {
    if (confirmReset) {
      onReset && onReset();
      setConfirmReset(false);
      
      // Show success toast
      setToastMessage(language === 'es' 
        ? '✅ Plan reiniciado correctamente' 
        : '✅ Plan reset successfully');
      setToastType('success');
      setShowToast(true);
    }
  };

  const handleSaveCapital = () => {
    const newValue = parseFloat(capitalInputValue) || 0;
    
    // If there's already capital set and user tries to change it, ask for confirmation
    if (initialCapital > 0 && newValue !== initialCapital) {
      setPendingCapitalValue(newValue);
      setConfirmCapitalChange(true);
    } else {
      // First time setting capital or no change
      if (newValue !== initialCapital) {
        onInitialCapitalChange && onInitialCapitalChange(newValue);
        setHasUnsavedCapital(false);
        
        setToastMessage(language === 'es' 
          ? '✅ Capital inicial guardado' 
          : '✅ Initial capital saved');
        setToastType('success');
        setShowToast(true);
      }
    }
  };

  const confirmCapitalChangeHandler = () => {
    if (pendingCapitalValue !== null) {
      onInitialCapitalChange && onInitialCapitalChange(pendingCapitalValue);
      setPendingCapitalValue(null);
      setConfirmCapitalChange(false);
      setHasUnsavedCapital(false);
      
      setToastMessage(language === 'es' 
        ? '✅ Capital inicial actualizado' 
        : '✅ Initial capital updated');
      setToastType('success');
      setShowToast(true);
    }
  };

  const confirmExtractionHandler = () => {
    if (pendingExtraction) {
      onConfirmExtraction && onConfirmExtraction(pendingExtraction.index, pendingExtraction.amount);
      
      setToastMessage(language === 'es' 
        ? `✅ Extracción de $${formatNumber(pendingExtraction.amount)} confirmada y descontada del total` 
        : `✅ Extraction of $${formatNumber(pendingExtraction.amount)} confirmed and discounted from total`);
      setToastType('success');
      setShowToast(true);
      setPendingExtraction(null);
    }
  };

  const undoExtractionHandler = () => {
    if (pendingUndoExtraction !== null) {
      onUndoExtraction && onUndoExtraction(pendingUndoExtraction);
      setPendingUndoExtraction(null);
      
      setToastMessage(language === 'es' 
        ? '↩️ Extracción revertida, el monto vuelve al total' 
        : '↩️ Extraction reverted, amount returned to total');
      setToastType('success');
      setShowToast(true);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="plan-table">
      
      {/* Toast Notification */}
      {showToast && (
        <Toast 
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
          duration={4000}
        />
      )}
      
      {/* Capital Change Confirmation Modal */}
      {confirmCapitalChange && (
        <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white p-6 rounded-xl shadow-2xl border-2 border-amber-200 max-w-sm w-full text-center space-y-4">
              <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto">
                <Target size={24} />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-gray-900">
                  {language === 'es' ? '¿Modificar Capital Inicial?' : 'Modify Initial Capital?'}
                </h3>
                <p className="text-sm text-gray-500">
                  {language === 'es' 
                    ? 'Esto afectará todos los cálculos de tu plan. ¿Estás seguro?' 
                    : 'This will affect all your plan calculations. Are you sure?'}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setConfirmCapitalChange(false);
                    setPendingCapitalValue(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-lg transition-colors text-sm"
                >
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={confirmCapitalChangeHandler}
                  className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg transition-colors text-sm"
                >
                  {language === 'es' ? 'Confirmar' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Extraction Confirmation Modal */}
        {pendingExtraction && (
          <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white p-6 rounded-xl shadow-2xl border-2 border-indigo-200 max-w-sm w-full text-center space-y-4">
              <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto">
                <Download size={24} />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-gray-900">
                  {language === 'es' ? '¿Confirmar Extracción?' : 'Confirm Extraction?'}
                </h3>
                <p className="text-sm text-gray-500">
                  {language === 'es' 
                    ? `Confirmas que retiraste $${formatNumber(pendingExtraction.amount)} de tu cuenta. Este monto se descontará del Total Cuenta.` 
                    : `You confirm you withdrew $${formatNumber(pendingExtraction.amount)} from your account. This amount will be discounted from the Total Account.`}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setPendingExtraction(null)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-lg transition-colors text-sm"
                  data-testid="cancel-extraction-btn"
                >
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={confirmExtractionHandler}
                  className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg transition-colors text-sm"
                  data-testid="confirm-extraction-btn"
                >
                  {language === 'es' ? 'Confirmar' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Undo Extraction Confirmation Modal */}
        {pendingUndoExtraction !== null && (
          <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white p-6 rounded-xl shadow-2xl border-2 border-amber-200 max-w-sm w-full text-center space-y-4">
              <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto">
                <RotateCcw size={24} />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-gray-900">
                  {language === 'es' ? '¿Revertir Extracción?' : 'Undo Extraction?'}
                </h3>
                <p className="text-sm text-gray-500">
                  {language === 'es' 
                    ? 'El monto extraído volverá a sumarse al Total Cuenta.' 
                    : 'The extracted amount will be added back to the Total Account.'}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setPendingUndoExtraction(null)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-lg transition-colors text-sm"
                  data-testid="cancel-undo-extraction-btn"
                >
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={undoExtractionHandler}
                  className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg transition-colors text-sm"
                  data-testid="confirm-undo-extraction-btn"
                >
                  {language === 'es' ? 'Revertir' : 'Undo'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Reset Confirmation Modal */}
        {confirmReset && (
          <ResetConfirmModal onCancel={() => setConfirmReset(false)} onConfirm={handleReset} />
        )}
        
        {/* Extra Capital Modal */}
        {showDepositsModal && (
          <ExtraCapitalModal
            extraDeposits={extraDeposits}
            onAdd={(amount, dateStr) => {
              if (onAddDeposit) {
                onAddDeposit(amount, dateStr);
                setToastMessage(language === 'es' ? `Capital extra de $${formatNumber(amount)} agregado` : `Extra capital of $${formatNumber(amount)} added`);
                setToastType('success');
                setShowToast(true);
              }
            }}
            onDeleteDeposit={onDeleteDeposit}
            onClose={() => setShowDepositsModal(false)}
          />
        )}
        
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100 gap-4 relative">
        <PlanHeader
          entriesCount={bitacoraEntries.length}
          isDownloading={isDownloading}
          onDownload={downloadAsCSV}
          onReset={() => setConfirmReset(true)}
          canUndo={canUndo}
          onUndo={onUndo}
          capitalInputValue={capitalInputValue}
          onCapitalChange={(value) => {
            setCapitalInputValue(value);
            setHasUnsavedCapital(Number(value) !== Number(initialCapital));
          }}
          hasUnsavedCapital={hasUnsavedCapital}
          onSaveCapital={handleSaveCapital}
          accountStats={accountStats}
          onOpenDeposits={() => setShowDepositsModal(true)}
          accountAdjustment={accountAdjustment}
          onEditTotal={(newTotal) => {
            if (!onAccountAdjustmentChange) return;
            // Use the UNCLAMPED total so the edited value is exactly what the user typed
            const newAdjustment = Number((accountAdjustment + (newTotal - accountStats.rawTotalAccount)).toFixed(2));
            onAccountAdjustmentChange(newAdjustment);
            setToastMessage(language === 'es' 
              ? `✅ Total Cuenta ajustado a $${formatNumber(newTotal)}` 
              : `✅ Total Account adjusted to $${formatNumber(newTotal)}`);
            setToastType('success');
            setShowToast(true);
          }}
          onResetAdjustment={() => {
            onAccountAdjustmentChange && onAccountAdjustmentChange(0);
            setToastMessage(language === 'es' 
              ? '↩️ Ajuste manual eliminado, se restauró el valor calculado' 
              : '↩️ Manual adjustment removed, calculated value restored');
            setToastType('success');
            setShowToast(true);
          }}
        />
      </div>

      {/* Pending Withdrawal Reminder */}
      {pendingExtractions.count > 0 && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="pending-extractions-reminder">
          <div className="flex items-start gap-3">
            <div className="bg-amber-100 p-2 rounded-full shrink-0">
              <Download size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="font-bold text-sm sm:text-base text-amber-900">
                ⚠️ {language === 'es' 
                  ? `Tienes ${pendingExtractions.count} extracci${pendingExtractions.count > 1 ? 'ones' : 'ón'} sin confirmar por $${formatNumber(pendingExtractions.total)}` 
                  : `You have ${pendingExtractions.count} unconfirmed extraction${pendingExtractions.count > 1 ? 's' : ''} of $${formatNumber(pendingExtractions.total)}`}
              </p>
              <p className="text-amber-700 text-xs sm:text-sm">
                {language === 'es' 
                  ? 'Cuando retires el dinero de tu cuenta, presiona "Confirmar" en la fila de extracción para descontarlo del total.' 
                  : 'When you withdraw the money from your account, press "Confirm" on the extraction row to discount it from the total.'}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div ref={scrollRef} className="overflow-x-auto max-h-[65vh] overflow-y-auto" data-testid="plan-table-scroll">
          <table className="min-w-full text-[10px] sm:text-xs divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm">
              <tr>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{t.plan.trades}</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-bold text-emerald-600 uppercase whitespace-nowrap">{language === 'es' ? 'Total Cuenta' : 'Total Account'}</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{t.plan.investment}</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{language === 'es' ? 'Invertido Real' : 'Real Invested'}</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">%</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{t.plan.profit}</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{language === 'es' ? 'Profit Total' : 'Total Profit'}</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{t.plan.profitPlus}</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{t.plan.extraction}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {planRows.map((row) => (
                <tr 
                  key={row.index} 
                  ref={row.isCurrentTrade ? currentRowRef : null}
                  className={`transition-colors ${
                    row.isCurrentTrade 
                      ? 'bg-blue-50 border-l-4 border-blue-500 font-bold' 
                      : row.isExecuted 
                        ? row.streakBroken
                          ? 'bg-red-50/70 hover:bg-red-50 border-l-4 border-red-400'
                          : 'hover:bg-gray-50' 
                        : 'bg-gray-50 opacity-60'
                  }`}
                >
                  <td className="px-3 sm:px-4 py-3 sm:py-4 whitespace-nowrap font-bold text-gray-600">
                    {row.isCurrentTrade && (
                      <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
                    )}
                    {t.plan.trade} {row.index}
                    {row.isExecuted && (
                      <span className="block text-[9px] text-gray-400 font-normal">
                        {row.date}{row.opsCount > 1 ? ` · ${row.opsCount} ops` : ''}
                      </span>
                    )}
                    {row.isExecuted && (
                      row.streakBroken ? (
                        <span className="inline-block mt-0.5 bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full text-[8px] sm:text-[9px] font-black" data-testid={`streak-broken-${row.index}`}>
                          ✕ {language === 'es' ? 'racha rota' : 'streak broken'}
                        </span>
                      ) : (
                        <span className="inline-block mt-0.5 bg-emerald-100 text-emerald-600 px-1.5 py-0.5 rounded-full text-[8px] sm:text-[9px] font-black" data-testid={`streak-count-${row.index}`}>
                          🔥 {((row.streak - 1) % 5) + 1}/5
                        </span>
                      )
                    )}
                  </td>
                  <td className={`px-3 sm:px-4 py-3 sm:py-4 whitespace-nowrap font-bold ${row.isExecuted ? (row.accountTotal >= initialCapital ? 'text-emerald-700' : 'text-red-600') : row.isCurrentTrade ? 'text-blue-700' : 'text-gray-400 font-medium'}`} data-testid={`plan-account-total-${row.index}`}>
                    ${formatNumber(row.accountTotal)}
                  </td>
                  <td className="px-3 sm:px-4 py-3 sm:py-4 whitespace-nowrap font-medium text-gray-900">
                    ${formatNumber(row.investment)}
                  </td>
                  <td className="px-3 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-gray-500" data-testid={`plan-invested-real-${row.index}`}>
                    {row.isExecuted ? `$${formatNumber(row.investedReal)}` : '-'}
                  </td>
                  <td className="px-3 sm:px-4 py-3 sm:py-4 whitespace-nowrap font-medium text-blue-600">{percentage}%</td>
                  <td className={`px-3 sm:px-4 py-3 sm:py-4 whitespace-nowrap font-medium ${row.streakBroken ? 'text-red-600 font-bold' : 'text-emerald-600'}`}>
                    {formatMoney(row.streakBroken ? row.realProfit : row.expectedProfit)}
                  </td>
                  <td className={`px-3 sm:px-4 py-3 sm:py-4 whitespace-nowrap font-bold ${row.realProfit >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid={`plan-profit-total-${row.index}`}>
                    {row.isExecuted ? formatMoney(row.realProfit) : '-'}
                  </td>
                  <td className={`px-3 sm:px-4 py-3 sm:py-4 whitespace-nowrap font-bold ${row.profitPlus >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {row.isExecuted && !row.streakBroken && row.profitPlus > 0 ? `$${formatNumber(row.profitPlus)}` : '-'}
                  </td>
                  <td className="px-3 sm:px-4 py-3 sm:py-4 whitespace-nowrap">
                    {(row.extraction !== 0 || confirmedExtractions[row.index]) ? (
                      confirmedExtractions[row.index] ? (
                        <button
                          onClick={() => setPendingUndoExtraction(row.index)}
                          className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-bold text-[10px] sm:text-xs hover:bg-emerald-200 transition-colors"
                          title={language === 'es' ? 'Retirado - Toca para revertir' : 'Extracted - Tap to undo'}
                          data-testid={`extraction-done-${row.index}`}
                        >
                          <Check size={12} />
                          ${formatNumber(confirmedExtractions[row.index].amount)}
                          <span className="hidden sm:inline">{language === 'es' ? 'Retirado' : 'Extracted'}</span>
                        </button>
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full font-bold text-[10px] sm:text-xs">
                              ${formatNumber(row.extraction)}
                            </span>
                            {row.extraction > 0 && (
                              <button
                                onClick={() => setPendingExtraction({ index: row.index, amount: row.extraction })}
                                className="px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg text-[10px] sm:text-xs transition-colors whitespace-nowrap active:scale-95"
                                data-testid={`confirm-extraction-btn-${row.index}`}
                              >
                                {language === 'es' ? 'Confirmar' : 'Confirm'}
                              </button>
                            )}
                          </div>
                          {row.extractionReduced && (
                            <span className="text-[9px] sm:text-[10px] text-amber-600 font-semibold" data-testid={`extraction-reduced-${row.index}`}>
                              {language === 'es' 
                                ? `Reducida por pérdida (era $${formatNumber(row.originalExtraction)})` 
                                : `Reduced by loss (was $${formatNumber(row.originalExtraction)})`}
                            </span>
                          )}
                        </div>
                      )
                    ) : row.extractionCancelled ? (
                      <div className="flex flex-col gap-0.5" data-testid={`extraction-cancelled-${row.index}`}>
                        <span className="bg-red-100 text-red-600 px-2 py-1 rounded-full font-bold text-[10px] sm:text-xs w-fit">
                          ${formatNumber(row.cutBalance)}
                        </span>
                        <span className="text-[9px] sm:text-[10px] text-red-500 font-semibold">
                          {language === 'es' ? 'Sin extracción: pérdida posterior superó lo ganado' : 'No extraction: later loss exceeded gains'}
                        </span>
                      </div>
                    ) : (row.cutBalance !== null && row.cutBalance <= 0) ? (
                      <div className="flex flex-col gap-0.5" data-testid={`no-extraction-${row.index}`}>
                        <span className="bg-red-100 text-red-600 px-2 py-1 rounded-full font-bold text-[10px] sm:text-xs w-fit">
                          ${formatNumber(row.cutBalance)}
                        </span>
                        <span className="text-[9px] sm:text-[10px] text-red-500 font-semibold">
                          {language === 'es' ? 'Sin extracción en este corte' : 'No extraction this cut'}
                        </span>
                      </div>
                    ) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Extraction History */}
      {Object.keys(confirmedExtractions).length > 0 && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden" data-testid="extraction-history">
          <div className="px-4 sm:px-6 py-3 sm:py-4 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between">
            <h3 className="text-sm sm:text-base font-bold text-indigo-900 flex items-center gap-2">
              <Download size={18} className="text-indigo-600" />
              {language === 'es' ? 'Historial de Retiros' : 'Withdrawal History'}
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm font-bold text-indigo-700 bg-indigo-100 px-3 py-1 rounded-full">
                {language === 'es' ? 'Total: ' : 'Total: '}${formatNumber(accountStats.totalExtracted)}
              </span>
              <button
                onClick={downloadHistoryCSV}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm hover:shadow-md transition-all active:scale-95"
                title={language === 'es' ? 'Descargar historial de retiros en CSV' : 'Download withdrawal history as CSV'}
                data-testid="download-history-csv-btn"
              >
                <Download size={14} />
                CSV
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-[10px] sm:text-xs divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">#</th>
                  <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{t.plan.trade}</th>
                  <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{language === 'es' ? 'Fecha de Retiro' : 'Withdrawal Date'}</th>
                  <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{language === 'es' ? 'Monto' : 'Amount'}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedExtractions
                  .map(([tradeIndex, ex], i) => (
                    <tr key={tradeIndex} className="hover:bg-gray-50 transition-colors" data-testid={`extraction-history-row-${tradeIndex}`}>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap font-bold text-gray-400">{i + 1}</td>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap font-bold text-gray-600">{t.plan.trade} {tradeIndex}</td>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-gray-700">
                        {new Date(ex.date).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                          day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </td>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-bold">
                          <Check size={12} />
                          ${formatNumber(ex.amount)}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
